/**
 * languageGate.js — PIPE-LANG-1 OUTPUT-SIDE enforcement.
 *
 * The invariant: a story interviewed in language X saves in language X. This is the FIFTH recurrence
 * of a wrong-language save; every prior fix was prompt-side (a language directive), which per our own
 * rule is not enforcement. This gate validates the polished OUTPUT against the SOURCE and, on a
 * confident mismatch, drives one regen (forcing the SOURCE language — never hardcoded English, so
 * TB-097 Spanish speakers are not broken) then falls back to a language-safe version. The raw answers
 * are the user's own words by construction, so the ultimate fallback is always correct-language.
 *
 * Detection is coarse (function-word density) and deliberately conservative: 'unknown' on either side
 * means DO NOT block (avoids false positives on genuinely ambiguous / multilingual input). It reuses
 * aiPrompts' ENGLISH_MARKERS (one English source; the interviewEngine.js copy remains a tracked drift
 * folded into this invariant). Encoding-robust: relies on ASCII function words, so mojibako'd accents
 * (á -> mangled) do not defeat it.
 */
import { ENGLISH_MARKERS } from './aiPrompts.js';

// Common Spanish function words + a few high-frequency content words. ASCII where possible so accent
// corruption in stored content does not defeat detection.
const SPANISH_MARKERS = new Set([
  'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'de', 'del', 'al', 'que', 'y', 'en', 'con',
  'por', 'para', 'su', 'sus', 'mi', 'mis', 'lo', 'le', 'les', 'se', 'no', 'como', 'pero', 'mas',
  'muy', 'cuando', 'donde', 'porque', 'fue', 'era', 'esta', 'este', 'ella', 'ellos', 'ellas',
  'madre', 'padre', 'hijo', 'hija', 'vida', 'siempre', 'tambien', 'habia', 'tenia', 'sobre',
]);

const MIN_WORDS = 3;      // below this, too short to judge — 'unknown'
const DENSITY_FLOOR = 0.12;

/**
 * Coarse language of a story: 'en' | 'es' | 'unknown'. 'unknown' = not confident (gate passes).
 */
export function detectStoryLanguage(text) {
  const words = (text || '').toLowerCase().match(/[a-záéíóúñ']+/g) || [];
  if (words.length < MIN_WORDS) return 'unknown';
  let en = 0, es = 0;
  for (const w of words) {
    if (ENGLISH_MARKERS.has(w)) en++;
    if (SPANISH_MARKERS.has(w)) es++;
  }
  const enD = en / words.length, esD = es / words.length;
  if (enD >= DENSITY_FLOOR && enD > esD) return 'en';
  if (esD >= DENSITY_FLOOR && esD > enD) return 'es';
  return 'unknown';
}

/**
 * The gate: is the output in the same language as the source? Blocks ONLY on a confident mismatch
 * (both sides known and different). Returns { ok, sourceLang, outputLang }.
 */
export function languageGate({ sourceText, outputText }) {
  const sourceLang = detectStoryLanguage(sourceText);
  const outputLang = detectStoryLanguage(outputText);
  const ok = !(sourceLang !== 'unknown' && outputLang !== 'unknown' && sourceLang !== outputLang);
  return { ok, sourceLang, outputLang };
}

const LANGUAGE_NAME = { en: 'English', es: 'Spanish' };

/**
 * A regen directive that forces the SOURCE language (not English). Appended to the polish prompt on a
 * gate failure. Empty when the source language is unknown (nothing to force).
 */
export function forceLanguageDirective(lang) {
  const name = LANGUAGE_NAME[lang];
  if (!name) return '';
  return `\n\nCRITICAL LANGUAGE RULE: The person spoke in ${name}. Write the title AND the entire story in ${name}. Do NOT translate it into any other language.`;
}
